import { useCallback, useEffect } from 'react';
import {
	createDefaultEocWrapper,
	createActionsHook,
	createHook,
	createStore,
	createContainer,
} from '@atlassian/eoc-rss-helpers';
import { useFlagService } from '@atlassian/jira-flags';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	ADMIN_CONSENT_REQUIRED_EVENT_ID,
	MSTEAMS_CREATE_CONNECTION_EVENT_ID,
	MSTEAMS_USER_LEVEL_AUTH_EVENT_ID,
	SLACK_CREATE_CONNECTION_EVENT_ID,
	SLACK_USER_LEVEL_AUTH_EVENT_ID,
	emptyResource,
} from '../../../common/constants';
import type { StoreBaseContainerProps } from '../../../common/types';
import { useMSTConnection } from '../../use-mst-connection';
import { useSlackConnection } from '../../use-slack-connection';
import { actions } from './actions';
import type { ChatToolsState } from './types';

export const initialState: ChatToolsState = {
	connections: emptyResource,
	integrations: emptyResource,
	loadingConnectionId: '',
	loadingIntegrationId: '',
	mstConnectionUrls: {},
};

const Store = createStore({ initialState, actions, name: 'chat-tools' });

const ChatToolsContainer = createContainer<
	ChatToolsState,
	typeof actions,
	{ children?: React.ReactNode; isGlobal?: boolean }
>(Store, {
	Wrapper: createDefaultEocWrapper({
		useSyncContainerProps: (): StoreBaseContainerProps => {
			const { cloudId } = useTenantContext();
			const { showFlag } = useFlagService();
			return {
				cloudId,
				showFlag,
			};
		},
		useContainerHook: () => {
			const { initialize } = useChatToolsActions();
			const { slackConnectHandler, slackUserLevelAuthoriseHandler } = useSlackConnection({
				onSuccess: initialize,
			});
			const { mstConnectHandler, adminAuthoriseHandler, mstUserLevelAuthoriseHandler } =
				useMSTConnection({
					onSuccess: initialize,
				});

			const connectHandler = useCallback(
				(event: MessageEvent) => {
					if (!event.data || !event.data.event_id) {
						return;
					}

					if (event.data.event_id === SLACK_CREATE_CONNECTION_EVENT_ID) {
						slackConnectHandler(event);
					} else if (event.data.event_id === MSTEAMS_CREATE_CONNECTION_EVENT_ID) {
						mstConnectHandler(event);
					} else if (event.data.event_id === ADMIN_CONSENT_REQUIRED_EVENT_ID) {
						adminAuthoriseHandler(event);
					} else if (event.data.event_id === MSTEAMS_USER_LEVEL_AUTH_EVENT_ID) {
						mstUserLevelAuthoriseHandler(event);
					} else if (event.data.event_id === SLACK_USER_LEVEL_AUTH_EVENT_ID) {
						slackUserLevelAuthoriseHandler(event);
					}
				},
				[
					slackConnectHandler,
					mstConnectHandler,
					adminAuthoriseHandler,
					mstUserLevelAuthoriseHandler,
					slackUserLevelAuthoriseHandler,
				],
			);

			useEffect(() => {
				window.addEventListener('message', connectHandler);

				return () => {
					window.removeEventListener('message', connectHandler);
				};
			}, [connectHandler]);
		},
	}),
});

const useChatToolsStore = createHook(Store);
const useChatToolsActions = createActionsHook(Store);

const useChatToolsConnections = createHook(Store, {
	selector: (state) => state.connections,
});

const useChatToolsIntegrations = createHook(Store, {
	selector: (state) => state.integrations,
});

export {
	useChatToolsStore,
	useChatToolsActions,
	useChatToolsConnections,
	useChatToolsIntegrations,
	ChatToolsContainer,
};
